import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { masterUsersApi } from '@/api/masterUsers.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Select } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { PlatformUserResponse } from '@/types/platformUser';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';

type UserFilter = 'all' | 'master' | 'tenant';

export function MasterUsersPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<PlatformUserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<UserFilter>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterUsersApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
        activeOnly: false,
        masterOnly: filter === 'all' ? undefined : filter === 'master',
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, filter, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns: Column<PlatformUserResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => `${r.firstName} ${r.lastName ?? ''}`.trim(),
    },
    { key: 'email', header: 'Email' },
    {
      key: 'organizationName',
      header: 'Organization',
      render: (r) => (r.masterAdmin ? '— Platform —' : (r.organizationName ?? '—')),
    },
    {
      key: 'roleName',
      header: 'Role',
      render: (r) => r.roleName ?? (r.masterAdmin ? 'Master Admin' : '—'),
    },
    {
      key: 'masterAdmin',
      header: 'Type',
      render: (r) => (r.masterAdmin ? 'Master' : 'Tenant'),
    },
    {
      key: 'emailVerified',
      header: 'Verified',
      render: (r) => (r.emailVerified ? 'Yes' : 'No'),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <span className={r.active ? 'text-green-600' : 'text-red-600'}>{r.active ? 'Active' : 'Inactive'}</span>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last login',
      render: (r) => (r.lastLogin ? formatDate(r.lastLogin) : '—'),
    },
    { key: 'createdAt', header: 'Created', render: (r) => formatDate(r.createdAt) },
  ];

  return (
    <div className="page-content">
      <PageHeader
        title="Platform users"
        description="Track sign-in accounts across all organizations and master operators"
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Users' }]}
        actions={
          <Link
            to="/master/admins"
            className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Manage master admins
          </Link>
        }
      />

      <div className="mb-4 w-56">
        <Select
          label="Account type"
          value={filter}
          onChange={(value) => {
            pagination.setPage(0);
            setFilter(value as UserFilter);
          }}
          options={[
            { value: 'all', label: 'All users' },
            { value: 'master', label: 'Master admins only' },
            { value: 'tenant', label: 'Tenant users only' },
          ]}
        />
      </div>

      <DataTable
        columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        searchPlaceholder="Search email, name…"
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        emptyMessage="No users found"
      />
    </div>
  );
}
